import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Search, Edit, Trash2, FileCheck, FileX, AlertCircle } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GSTIN_LENGTH = 15;

const ClientRow = ({ client, onEdit, onDelete }) => (
  <tr data-testid={`client-row-${client.id}`}>
    <td className="font-medium text-slate-900">{client.name}</td>
    <td className="font-mono text-sm">{client.gstin}</td>
    <td>{client.state_code}</td>
    <td>
      {client.e_invoice_applicable ? (
        <span className="inline-flex items-center gap-1 text-green-600">
          <FileCheck className="w-4 h-4" />Yes
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-slate-400">
          <FileX className="w-4 h-4" />No
        </span>
      )}
    </td>
    <td>
      <div className="text-sm">
        {client.contact_person && <p>{client.contact_person}</p>}
        {client.phone && <p className="text-slate-500">{client.phone}</p>}
      </div>
    </td>
    <td>
      <div className="flex items-center gap-2">
        <button onClick={() => onEdit(client)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-sm transition-colors" data-testid={`edit-client-${client.id}`}>
          <Edit className="w-4 h-4" />
        </button>
        <button onClick={() => onDelete(client.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-sm transition-colors" data-testid={`delete-client-${client.id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </td>
  </tr>
);

const ClientForm = ({ formData, setFormData, onSubmit, error, editingClient, onClose }) => (
  <form onSubmit={onSubmit} className="space-y-4 mt-4">
    {error && (
      <div className="p-3 bg-red-50 border border-red-200 rounded-sm flex items-center gap-2 text-red-700 text-sm">
        <AlertCircle className="w-4 h-4" />{error}
      </div>
    )}
    <div>
      <Label htmlFor="name">Client Name *</Label>
      <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="mt-1.5 rounded-sm" required data-testid="client-name-input" />
    </div>
    <div>
      <Label htmlFor="gstin">GSTIN *</Label>
      <Input
        id="gstin"
        value={formData.gstin}
        onChange={(e) => {
          const value = e.target.value.toUpperCase();
          setFormData({ ...formData, gstin: value, state_code: value.length >= 2 ? value.substring(0, 2) : '' });
        }}
        className="mt-1.5 rounded-sm font-mono"
        placeholder="29AAACK1234A1Z5"
        maxLength={GSTIN_LENGTH}
        required
        data-testid="client-gstin-input"
      />
    </div>
    <div className="flex items-center space-x-2">
      <Checkbox id="e_invoice" checked={formData.e_invoice_applicable} onCheckedChange={(checked) => setFormData({ ...formData, e_invoice_applicable: checked })} data-testid="client-einvoice-checkbox" />
      <Label htmlFor="e_invoice" className="text-sm">E-Invoice Applicable</Label>
    </div>
    <div>
      <Label htmlFor="contact_person">Contact Person</Label>
      <Input id="contact_person" value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} className="mt-1.5 rounded-sm" data-testid="client-contact-input" />
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label htmlFor="phone">Phone</Label>
        <Input id="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="mt-1.5 rounded-sm" data-testid="client-phone-input" />
      </div>
      <div>
        <Label htmlFor="email">Email</Label>
        <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="mt-1.5 rounded-sm" data-testid="client-email-input" />
      </div>
    </div>
    <div className="flex justify-end gap-3 pt-4">
      <Button type="button" variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
      <Button type="submit" className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-sm" data-testid="client-save-button">
        {editingClient ? 'Update' : 'Save'}
      </Button>
    </div>
  </form>
);

const emptyFormData = { name: '', gstin: '', state_code: '', e_invoice_applicable: false, contact_person: '', phone: '', email: '' };

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState(emptyFormData);
  const [error, setError] = useState('');

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/clients`, { withCredentials: true });
      setClients(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingClient) {
        await axios.put(`${BACKEND_URL}/api/clients/${editingClient.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/api/clients`, formData, { withCredentials: true });
      }
      handleCloseDialog();
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save client');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      gstin: client.gstin,
      state_code: client.state_code,
      e_invoice_applicable: client.e_invoice_applicable,
      contact_person: client.contact_person || '',
      phone: client.phone || '',
      email: client.email || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/clients/${clientId}`, { withCredentials: true });
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete client');
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingClient(null);
    setFormData(emptyFormData);
    setError('');
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.gstin.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="clients-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Clients</h1>
            <p className="text-slate-500 mt-1">Manage your GST clients</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) handleCloseDialog(); else setIsDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-sm" data-testid="add-client-button">
                <Plus className="w-4 h-4 mr-2" />Add Client
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" aria-describedby="client-form-desc">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>{editingClient ? 'Edit Client' : 'Add New Client'}</DialogTitle>
                <p id="client-form-desc" className="text-sm text-slate-500 mt-1">{editingClient ? 'Update client details' : 'Enter client details with valid GSTIN'}</p>
              </DialogHeader>
              <ClientForm formData={formData} setFormData={setFormData} onSubmit={handleSubmit} error={error} editingClient={editingClient} onClose={handleCloseDialog} />
            </DialogContent>
          </Dialog>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search by name or GSTIN..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 rounded-sm" data-testid="client-search-input" />
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="clients-table">
              <thead>
                <tr>
                  <th>Client Name</th>
                  <th>GSTIN</th>
                  <th>State</th>
                  <th>E-Invoice</th>
                  <th>Contact</th>
                  <th className="w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  if (loading) return <tr><td colSpan={6} className="text-center py-8"><div className="flex items-center justify-center gap-2 text-slate-500"><div className="spinner" />Loading clients...</div></td></tr>;
                  if (filteredClients.length === 0) return <tr><td colSpan={6} className="text-center py-8 text-slate-500">No clients found</td></tr>;
                  return filteredClients.map((client) => <ClientRow key={client.id} client={client} onEdit={handleEdit} onDelete={handleDelete} />);
                })()}
              </tbody>
            </table>
          </div>
        </div>

        <div className="text-sm text-slate-500">Showing {filteredClients.length} of {clients.length} clients</div>
      </div>
    </Layout>
  );
};

export default Clients;
